import { useEffect } from 'react';

import { FormProvider } from '@toolbox/form';
import { StepContextProvider } from './StepContext';
import { useStepForm } from './useStepForm';
import { useEventPipeline } from './EventsProvider';
import { useWidgetContext } from './WidgetContext';

import type { StepProps, UseStepFormReturn } from './useStepForm';
import type { FieldValues } from '@toolbox/form';
import type { ReactNode } from 'react';

declare module './EventsProvider' {
  export interface WidgetEvents {
    STEP_MOUNT: { id: string; index: number };
  }
}

type StepProviderProps<FD extends FieldValues = FieldValues> = UseStepFormReturn<FD> & {
  children: ReactNode;
  className?: string;
};

function StepProvider<FD extends FieldValues = FieldValues>({
  formMethods,
  children,
  className,
  ...stepProps
}: StepProviderProps<FD>): JSX.Element {
  return (
    <FormProvider {...formMethods} className={className}>
      <StepContextProvider {...stepProps}>{children}</StepContextProvider>
    </FormProvider>
  );
}

/**
 * Provides the functionalities to work with a widget step.
 */
export const Step = <FD extends FieldValues = FieldValues>({
  children,
  ...props
}: StepProps & { children?: ReactNode; className?: string }): JSX.Element => {
  const step = useStepForm<FD>(props);
  const widget = useWidgetContext();
  const event = useEventPipeline();

  useEffect(() => {
    widget.layoutSettings?.setHideHeaderText?.(!!props.hideHeaderText);
  }, [props.hideHeaderText, widget.layoutSettings]);

  useEffect(() => {
    event.dispatchOnce(`STEP_MOUNT:${props.id}`, {
      type: 'STEP_MOUNT',
      payload: { id: props.id, index: widget.history.findIndex(({ id }) => id === props.id) },
    });
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <StepProvider<FD> {...step}>{children}</StepProvider>;
};
